import { useState, useEffect } from "react";
import { base44 } from "@/api/base44Client";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import EmojiPicker from "@/components/EmojiPicker";


const EMPTY_CAT = { name: "", name_en: "", icon: "🧺", color: "from-cyan-400 to-blue-500", sort_order: 0, is_active: true };
const EMPTY_SVC = { name: "", name_en: "", category_id: "", price: "", price_wash_only: "", price_iron_only: "", unit: "قطعة", unit_en: "", description: "", description_en: "", icon: "👕", is_active: true, sort_order: 0, sale_price: "", sale_start: "", sale_end: "" };

const GRADIENT_OPTIONS = [
  "from-cyan-400 to-blue-500",
  "from-blue-400 to-indigo-500",
  "from-orange-400 to-amber-500",
  "from-purple-400 to-violet-500",
  "from-rose-400 to-pink-500",
  "from-green-400 to-emerald-500",
  "from-teal-400 to-cyan-500",
  "from-yellow-400 to-orange-500",
];

const isOnSale = (s) => {
  if (!s.sale_price || !s.sale_start || !s.sale_end) return false;
  const today = new Date().toISOString().slice(0, 10);
  return today >= s.sale_start && today <= s.sale_end;
};

export default function LaundryServices() {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("categories");

  // Category form state
  const [showCatForm, setShowCatForm] = useState(false);
  const [catForm, setCatForm] = useState(EMPTY_CAT);
  const [editCatId, setEditCatId] = useState(null);
  const [savingCat, setSavingCat] = useState(false);

  // Service form state
  const [showSvcForm, setShowSvcForm] = useState(false);
  const [svcForm, setSvcForm] = useState(EMPTY_SVC);
  const [editSvcId, setEditSvcId] = useState(null);
  const [savingSvc, setSavingSvc] = useState(false);


  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const [cats, svcs] = await Promise.all([
      base44.entities.LaundryCategory.list("-sort_order"),
      base44.entities.LaundryService.list("-sort_order"),
    ]);
    setCategories(cats);
    setServices(svcs);
    setLoading(false);
  };

  // ── Category handlers ──
  const handleSaveCat = async (e) => {
    e.preventDefault();
    if (!catForm.name) return;
    setSavingCat(true);
    const data = { ...catForm, sort_order: Number(catForm.sort_order) || 0 };
    if (editCatId) {
      await base44.entities.LaundryCategory.update(editCatId, data);
    } else {
      await base44.entities.LaundryCategory.create(data);
    }
    setCatForm(EMPTY_CAT);
    setEditCatId(null);
    setShowCatForm(false);
    await load();
    setSavingCat(false);
  };

  const startEditCat = (c) => {
    setCatForm({ ...c });
    setEditCatId(c.id);
    setShowCatForm(true);
  };

  const toggleActiveCat = async (c) => {
    await base44.entities.LaundryCategory.update(c.id, { is_active: !c.is_active });
    setCategories(prev => prev.map(x => x.id === c.id ? { ...x, is_active: !x.is_active } : x));
  };

  // ── Service handlers ──
  const handleSaveSvc = async (e) => {
    e.preventDefault();
    if (!svcForm.name) return;
    setSavingSvc(true);
    const data = {
      ...svcForm,
      price: svcForm.price ? Number(svcForm.price) : null,
      price_wash_only: svcForm.price_wash_only ? Number(svcForm.price_wash_only) : null,
      price_iron_only: svcForm.price_iron_only ? Number(svcForm.price_iron_only) : null,
      sort_order: Number(svcForm.sort_order) || 0,
      sale_price: svcForm.sale_price ? Number(svcForm.sale_price) : null,
      sale_start: svcForm.sale_start || null,
      sale_end: svcForm.sale_end || null,
    };
    if (editSvcId) {
      await base44.entities.LaundryService.update(editSvcId, data);
    } else {
      await base44.entities.LaundryService.create(data);
    }
    setSvcForm(EMPTY_SVC);
    setEditSvcId(null);
    setShowSvcForm(false);
    await load();
    setSavingSvc(false);
  };

  const startEditSvc = (s) => {
    setSvcForm({ ...s, price_wash_only: s.price_wash_only || "", price_iron_only: s.price_iron_only || "", sale_price: s.sale_price || "", sale_start: s.sale_start || "", sale_end: s.sale_end || "" });
    setEditSvcId(s.id);
    setShowSvcForm(true);
  };

  const toggleActiveSvc = async (s) => {
    await base44.entities.LaundryService.update(s.id, { is_active: !s.is_active });
    setServices(prev => prev.map(x => x.id === s.id ? { ...x, is_active: !x.is_active } : x));
  };

  const [searchQuery, setSearchQuery] = useState("");

  const getCatName = (id) => categories.find(c => c.id === id)?.name || "—";

  const filteredServices = searchQuery.trim()
    ? services.filter(s =>
        s.name?.includes(searchQuery) ||
        s.name_en?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.description?.includes(searchQuery) ||
        getCatName(s.category_id).includes(searchQuery)
      )
    : services;

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-5">
          <h1 className="text-2xl font-black text-foreground">🧺 الخدمات والفئات</h1>

          {/* Tabs */}
          <div className="flex gap-2 bg-secondary rounded-2xl p-1.5">
            {[
              { key: "categories", label: "📁 إدارة الفئات" },
              { key: "services", label: "🧾 إدارة الخدمات" },
            ].map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all ${activeTab === t.key ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="space-y-2">{[1, 2, 3].map(i => <div key={i} className="h-14 bg-card rounded-2xl animate-pulse" />)}</div>
          ) : (
            <>
              {/* ── Categories Tab ── */}
              {activeTab === "categories" && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-muted-foreground">{categories.length} فئة</p>
                    <button
                      onClick={() => { setShowCatForm(!showCatForm); setEditCatId(null); setCatForm(EMPTY_CAT); }}
                      className="px-4 py-2 bg-primary text-primary-foreground rounded-xl text-sm font-bold hover:bg-primary/90 transition-all"
                    >
                      + إضافة فئة
                    </button>
                  </div>

                  {showCatForm && (
                    <form onSubmit={handleSaveCat} className="bg-card rounded-2xl p-5 border border-border shadow-sm space-y-3">
                      <h3 className="font-bold text-foreground">{editCatId ? "✏️ تعديل فئة" : "➕ فئة جديدة"}</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-bold text-muted-foreground block mb-1">اسم الفئة (عربي) *</label>
                          <input value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                            className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-primary" placeholder="مثال: ملابس رجالية" />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-muted-foreground block mb-1">Category Name (English)</label>
                          <input value={catForm.name_en || ""} onChange={e => setCatForm(f => ({ ...f, name_en: e.target.value }))}
                            className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-primary" placeholder="English name..." dir="ltr" />

                        </div>
                        <div>
                          <label className="text-xs font-bold text-muted-foreground block mb-1">الأيقونة</label>
                          <EmojiPicker value={catForm.icon} onChange={v => setCatForm(f => ({ ...f, icon: v }))} placeholder="اختر أيقونة الفئة" />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-muted-foreground block mb-1">الترتيب</label>
                          <input type="number" value={catForm.sort_order} onChange={e => setCatForm(f => ({ ...f, sort_order: e.target.value }))}
                            className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-primary" placeholder="0" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-bold text-muted-foreground block mb-2">لون الفئة</label>
                        <div className="flex flex-wrap gap-2">
                          {GRADIENT_OPTIONS.map(g => (
                            <button
                              type="button"
                              key={g}
                              onClick={() => setCatForm(f => ({ ...f, color: g }))}
                              className={`w-10 h-10 rounded-xl bg-gradient-to-r ${g} transition-all ${catForm.color === g ? "ring-2 ring-primary ring-offset-2 scale-110" : "opacity-70"}`}
                            />
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <button type="submit" disabled={savingCat} className="flex-1 bg-primary text-primary-foreground font-bold py-2.5 rounded-xl hover:bg-primary/90 disabled:opacity-60 text-sm">
                          {savingCat ? "⏳ جاري الحفظ..." : "💾 حفظ"}
                        </button>
                        <button type="button" onClick={() => setShowCatForm(false)} className="px-5 bg-secondary rounded-xl text-sm font-bold">إلغاء</button>
                      </div>
                    </form>
                  )}

                  {categories.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <div className="text-4xl mb-2">📁</div>
                      <p>لا توجد فئات بعد. أضف فئة للبدء!</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {categories.map(c => (
                        <div key={c.id} className="bg-card rounded-2xl px-4 py-3 border border-border flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl bg-gradient-to-r ${c.color || "from-cyan-400 to-blue-500"} flex items-center justify-center text-xl`}>
                              {c.icon || "🧺"}
                            </div>
                            <div>
                              <p className={`font-bold text-sm ${c.is_active ? "text-foreground" : "text-muted-foreground line-through"}`}>{c.name}</p>
                              <p className="text-xs text-muted-foreground">{services.filter(s => s.category_id === c.id).length} خدمة</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => startEditCat(c)} className="text-xs px-3 py-1.5 bg-secondary rounded-lg hover:bg-secondary/80 font-medium">✏️</button>
                            <button onClick={() => toggleActiveCat(c)} className={`text-xs px-3 py-1.5 rounded-lg font-medium ${c.is_active ? "bg-red-100 dark:bg-red-900/30 text-red-600" : "bg-green-100 dark:bg-green-900/30 text-green-700"}`}>
                              {c.is_active ? "إخفاء" : "إظهار"}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ── Services Tab ── */}
              {activeTab === "services" && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <p className="text-sm text-muted-foreground">{filteredServices.length} / {services.length} خدمة</p>
                    <button
                      onClick={() => { setShowSvcForm(!showSvcForm); setEditSvcId(null); setSvcForm(EMPTY_SVC); }}
                      className="px-4 py-2 bg-cyan-600 text-white rounded-xl text-sm font-bold hover:bg-cyan-700 transition-all"
                    >
                      + إضافة خدمة
                    </button>
                  </div>

                  {/* Smart Search */}
                  <div className="relative">
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-base">🔍</span>
                    <input
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="ابحث بالاسم أو الفئة أو الوصف..."
                      className="w-full bg-background border border-border rounded-xl pr-9 pl-3 py-2 text-sm outline-none focus:border-cyan-500"
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery("")} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground text-xs">✕</button>
                    )}
                  </div>

                  {showSvcForm && (
                    <form onSubmit={handleSaveSvc} className="bg-card rounded-2xl p-5 border border-border shadow-sm space-y-3">
                      <h3 className="font-bold text-foreground">{editSvcId ? "✏️ تعديل خدمة" : "➕ خدمة جديدة"}</h3>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-bold text-muted-foreground block mb-1">اسم الخدمة (عربي) *</label>
                          <input value={svcForm.name} onChange={e => setSvcForm(f => ({ ...f, name: e.target.value }))}
                            className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500" placeholder="مثال: قميص" />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-muted-foreground block mb-1">Service Name (English)</label>
                          <input value={svcForm.name_en || ""} onChange={e => setSvcForm(f => ({ ...f, name_en: e.target.value }))}
                            className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500" placeholder="English name..." dir="ltr" />

                        </div>
                        <div className="col-span-2">
                          <label className="text-xs font-bold text-muted-foreground block mb-1">الفئة</label>
                          <select value={svcForm.category_id || ""} onChange={e => setSvcForm(f => ({ ...f, category_id: e.target.value }))}
                            className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500">
                            <option value="">— بدون فئة —</option>
                            {categories.filter(c => c.is_active).map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                          </select>
                        </div>
                      </div>

                      {/* Prices */}
                      <div className="border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-900/20 rounded-xl p-4 space-y-3">
                        <p className="text-xs font-black text-cyan-700 dark:text-cyan-400">💰 الأسعار <span className="font-normal text-muted-foreground">(اترك فارغاً إذا لم تكن الخدمة متاحة)</span></p>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="text-xs font-bold text-muted-foreground block mb-1">غسيل + كوي (ر.س)</label>
                            <input type="number" step="0.01" value={svcForm.price} onChange={e => setSvcForm(f => ({ ...f, price: e.target.value }))}
                              className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500" placeholder="0.00" />
                          </div>
                          <div>
                            <label className="text-xs font-bold text-muted-foreground block mb-1">غسيل فقط (ر.س)</label>
                            <input type="number" step="0.01" value={svcForm.price_wash_only} onChange={e => setSvcForm(f => ({ ...f, price_wash_only: e.target.value }))}
                              className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500" placeholder="0.00" />
                          </div>
                          <div>
                            <label className="text-xs font-bold text-muted-foreground block mb-1">كوي فقط (ر.س)</label>
                            <input type="number" step="0.01" value={svcForm.price_iron_only} onChange={e => setSvcForm(f => ({ ...f, price_iron_only: e.target.value }))}
                              className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500" placeholder="0.00" />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs font-bold text-muted-foreground block mb-1">الوحدة (عربي)</label>
                          <input value={svcForm.unit} onChange={e => setSvcForm(f => ({ ...f, unit: e.target.value }))}
                            className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500" placeholder="قطعة" />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-muted-foreground block mb-1">Unit (EN)</label>
                          <input value={svcForm.unit_en || ""} onChange={e => setSvcForm(f => ({ ...f, unit_en: e.target.value }))}
                            className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500" placeholder="e.g. piece" dir="ltr" />

                        </div>
                        <div>
                          <label className="text-xs font-bold text-muted-foreground block mb-1">أيقونة</label>
                          <EmojiPicker value={svcForm.icon} onChange={v => setSvcForm(f => ({ ...f, icon: v }))} placeholder="اختر أيقونة الخدمة" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-bold text-muted-foreground block mb-1">وصف الخدمة (عربي)</label>
                          <input value={svcForm.description} onChange={e => setSvcForm(f => ({ ...f, description: e.target.value }))}
                            className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500" placeholder="وصف الخدمة (اختياري)" />
                        </div>
                        <div>
                          <label className="text-xs font-bold text-muted-foreground block mb-1">Description (EN)</label>
                          <input value={svcForm.description_en || ""} onChange={e => setSvcForm(f => ({ ...f, description_en: e.target.value }))}
                            className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-cyan-500" placeholder="English description..." dir="ltr" />

                        </div>
                      </div>

                      {/* Sale */}
                      <div className="border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 space-y-3">
                        <p className="text-xs font-black text-amber-700 dark:text-amber-400">🏷️ عرض مؤقت بسعر مخفض (اختياري)</p>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="text-xs font-bold text-muted-foreground block mb-1">سعر العرض</label>
                            <input type="number" value={svcForm.sale_price} onChange={e => setSvcForm(f => ({ ...f, sale_price: e.target.value }))}
                              className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-amber-500" placeholder="0.00" />
                          </div>
                          <div>
                            <label className="text-xs font-bold text-muted-foreground block mb-1">من</label>
                            <input type="date" value={svcForm.sale_start} onChange={e => setSvcForm(f => ({ ...f, sale_start: e.target.value }))}
                              className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-amber-500" />
                          </div>
                          <div>
                            <label className="text-xs font-bold text-muted-foreground block mb-1">إلى</label>
                            <input type="date" value={svcForm.sale_end} onChange={e => setSvcForm(f => ({ ...f, sale_end: e.target.value }))}
                              className="w-full bg-background border border-border rounded-xl px-3 py-2 text-sm outline-none focus:border-amber-500" />
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <button type="submit" disabled={savingSvc} className="flex-1 bg-cyan-600 text-white font-bold py-2.5 rounded-xl hover:bg-cyan-700 disabled:opacity-60 text-sm">
                          {savingSvc ? "⏳ جاري الحفظ..." : "💾 حفظ"}
                        </button>
                        <button type="button" onClick={() => setShowSvcForm(false)} className="px-5 bg-secondary rounded-xl text-sm font-bold">إلغاء</button>
                      </div>
                    </form>
                  )}

                  {services.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <div className="text-4xl mb-2">🧾</div>
                      <p>لا توجد خدمات بعد</p>
                    </div>
                  ) : filteredServices.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">
                      <div className="text-4xl mb-2">🔍</div>
                      <p>لا توجد نتائج لـ "<strong>{searchQuery}</strong>"</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredServices.map(s => (
                        <div key={s.id} className="bg-card rounded-2xl px-4 py-3 border border-border flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{s.icon || "👕"}</span>
                            <div>
                              <p className={`font-bold text-sm ${s.is_active ? "text-foreground" : "text-muted-foreground line-through"}`}>{s.name}</p>
                              <p className="text-xs text-muted-foreground">
                                {getCatName(s.category_id)} •
                                {s.price ? <span className="text-cyan-600 dark:text-cyan-400 font-bold"> غسيل+كوي: {s.price} ر.س</span> : null}
                                {s.price_wash_only ? <span className="text-blue-500 font-bold"> • غسيل: {s.price_wash_only} ر.س</span> : null}
                                {s.price_iron_only ? <span className="text-orange-500 font-bold"> • كوي: {s.price_iron_only} ر.س</span> : null}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => startEditSvc(s)} className="text-xs px-3 py-1.5 bg-secondary rounded-lg hover:bg-secondary/80 font-medium">✏️</button>
                            <button onClick={() => toggleActiveSvc(s)} className={`text-xs px-3 py-1.5 rounded-lg font-medium ${s.is_active ? "bg-red-100 dark:bg-red-900/30 text-red-600" : "bg-green-100 dark:bg-green-900/30 text-green-700"}`}>
                              {s.is_active ? "إخفاء" : "إظهار"}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}